import { type Nullable } from '@Types';
import { createOutsideModal } from 'Modal';
import { type FrontRoyalRootScope } from 'FrontRoyalAngular';
import { angularInjectorProvider } from 'Injector';
import { type LearnerProject } from 'LearnerProjects';
import {
    type ProjectGroupMembershipSansProjectGroup,
    ProjectGroupSetupModal,
    ViewProjectGroupModal,
} from 'ProjectGroup';
import { convertCurrentUserFromIguana } from 'Users';
import { type AdminUploadTargetUser, type UploadProjectSubmissionResponse } from './ProjectSubmission.types';
import { ProjectSubmissionFormModal } from './ProjectSubmissionModal';

const createOutsideProjectGroupSetupModal = (project: LearnerProject) => {
    const close = createOutsideModal(
        angularInjectorProvider.injector!,
        <ProjectGroupSetupModal isOpen project={project} closeModal={() => close()} />,
    );
};

const createOutsideViewProjectGroupModal = (
    project: LearnerProject,
    membership: Nullable<ProjectGroupMembershipSansProjectGroup>,
) => {
    const close = createOutsideModal(
        angularInjectorProvider.injector!,
        <ViewProjectGroupModal
            isOpen
            project={project}
            closeModal={() => close()}
            projectGroupMembership={membership}
        />,
    );
};

// This is only used to create and show the React modal from Angular.
// We can get rid of this once the past-due-projects and schedule-box are recreated in React.
export const createOutsideProjectSubmissionModal = (project: LearnerProject) => {
    const currentUser = convertCurrentUserFromIguana(
        angularInjectorProvider.get<FrontRoyalRootScope>('$rootScope').currentUser || null,
    );
    const membership =
        (currentUser?.acceptedProjectGroupMemberships || []).find(
            m => m.requirementIdentifier === project.requirementIdentifier,
        ) || null;

    const close = createOutsideModal(
        angularInjectorProvider.injector!,
        <ProjectSubmissionFormModal
            isOpen
            closeModal={() => close()}
            project={project}
            projectGroupMembership={membership}
            onCreateGroup={() => {
                close();
                createOutsideProjectGroupSetupModal(project);
            }}
            onViewGroup={() => {
                close();
                createOutsideViewProjectGroupModal(project, membership);
            }}
        />,
    );
};

// No-op: Bootstrap 3's focus trapping has been removed along with the Bootstrap JS
// dependency. Our modal shim does not implement enforceFocus, so no workaround is needed.
// Kept as a callable no-op so callers don't need to change.
const disableBootstrapFocusTrap = (): (() => void) => () => {};

// Creates the submission modal in admin mode for uploading on behalf of a student.
// This is used from the grading portal evaluation modal.
export const createOutsideAdminProjectSubmissionModal = (
    project: LearnerProject,
    targetUser: AdminUploadTargetUser,
    cohortId: string,
    projectGroupMembership: Nullable<ProjectGroupMembershipSansProjectGroup>,
    onSuccess: (response: UploadProjectSubmissionResponse) => void,
) => {
    // Store the response so we can pass it to onSuccess when the modal closes
    let uploadResponse: UploadProjectSubmissionResponse | null = null;

    // Bootstrap 3 traps focus inside its modals, preventing input in React modals layered on top.
    const restoreBootstrapFocusTrap = disableBootstrapFocusTrap();

    const close = createOutsideModal(
        angularInjectorProvider.injector!,
        <ProjectSubmissionFormModal
            isOpen
            closeModal={() => {
                restoreBootstrapFocusTrap();
                close();
                if (uploadResponse) {
                    onSuccess(uploadResponse);
                }
            }}
            project={project}
            projectGroupMembership={projectGroupMembership}
            onCreateGroup={() => {}}
            onViewGroup={() => {}}
            targetUser={targetUser}
            cohortId={cohortId}
            onUploadSuccess={response => {
                uploadResponse = response;
            }}
            // Evaluation modal is at z-index 10001; this modal must stack above it
            modalClassNames={{ backdrop: 'z-[10002]', wrapper: 'z-[10003]' }}
        />,
    );
};
